import SynAudio from 'synaudio';
import WaveSurfer from 'wavesurfer.js';

export interface SyncResult {
  sampleOffset: number;
  correlation: number;
  timeOffset: number; // Offset in seconds
}

/**
 * Synchronizes two audio waveforms using cross-correlation
 * @param wavesurfer1 First WaveSurfer instance (reference)
 * @param wavesurfer2 Second WaveSurfer instance (to be synchronized)
 * @param options Optional configuration for early match preference
 * @returns Promise resolving to sync result with sample offset and correlation
 */
export async function synchronizeWaveforms(
  wavesurfer1: WaveSurfer,
  wavesurfer2: WaveSurfer,
  options?: { preferEarlyMatch?: boolean; earlyMatchThreshold?: number }
): Promise<SyncResult | null> {
  try {
    // Get decoded audio buffers from both WaveSurfer instances
    const audioBuffer1 = wavesurfer1.getDecodedData();
    const audioBuffer2 = wavesurfer2.getDecodedData();

    if (!audioBuffer1 || !audioBuffer2) {
      console.warn('[WaveformSync] One or both audio buffers are not available');
      return null;
    }

    // Get sample rate (should be the same for both)
    const sampleRate = audioBuffer1.sampleRate;
    if (audioBuffer2.sampleRate !== sampleRate) {
      console.warn('[WaveformSync] Sample rates differ, resampling may be needed');
    }

    // Get channel data (use first channel for mono, or mix stereo to mono)
    const getChannelData = (buffer: AudioBuffer, channelIndex: number = 0): Float32Array => {
      if (buffer.numberOfChannels > channelIndex) {
        return buffer.getChannelData(channelIndex);
      }
      // Fallback to first channel if requested channel doesn't exist
      return buffer.getChannelData(0);
    };

    // For stereo tracks, we can use both channels or mix them
    // Using left channel (channel 0) for simplicity - can be enhanced to use both channels
    const channel1Left = getChannelData(audioBuffer1, 0);
    const channel2Left = getChannelData(audioBuffer2, 0);

    // If stereo, optionally use right channel too (average both channels)
    let channel1Data: Float32Array;
    let channel2Data: Float32Array;

    if (audioBuffer1.numberOfChannels > 1 && audioBuffer2.numberOfChannels > 1) {
      // Mix stereo to mono for better correlation
      const channel1Right = getChannelData(audioBuffer1, 1);
      const channel2Right = getChannelData(audioBuffer2, 1);
      
      channel1Data = new Float32Array(channel1Left.length);
      channel2Data = new Float32Array(channel2Left.length);
      
      for (let i = 0; i < channel1Left.length; i++) {
        channel1Data[i] = (channel1Left[i] + channel1Right[i]) / 2;
      }
      for (let i = 0; i < channel2Left.length; i++) {
        channel2Data[i] = (channel2Left[i] + channel2Right[i]) / 2;
      }
    } else {
      channel1Data = channel1Left;
      channel2Data = channel2Left;
    }

    // Initialize SynAudio
    const synAudio = new SynAudio({
      correlationSampleSize: 5000, // Number of samples to use for correlation
      initialGranularity: 16, // Initial search granularity
    });

    // Prepare data for synaudio (full files)
    const base = {
      channelData: [channel1Data],
      samplesDecoded: channel1Data.length,
    };

    const comparison = {
      channelData: [channel2Data],
      samplesDecoded: channel2Data.length,
    };

    // Perform synchronization using Web Workers for parallel processing
    // Use 4 worker threads for better performance
    const { sampleOffset, correlation } = await synAudio.syncWorkerConcurrent(
      base,
      comparison,
      4
    );

    // Convert sample offset to time offset
    const timeOffset = sampleOffset / sampleRate;

    // If preferEarlyMatch is enabled, also search for matches in early portions
    let bestResult: SyncResult = {
      sampleOffset,
      correlation,
      timeOffset,
    };

    if (options?.preferEarlyMatch) {
      const earlyMatchThreshold = options.earlyMatchThreshold ?? 0.9; // Default 90% threshold
      
      // Calculate how early this match occurs in both tracks
      // For positive offset: match occurs at offset in reference, at 0 in comparison
      // For negative offset: match occurs at 0 in reference, at -offset in comparison
      const referenceMatchTime = timeOffset > 0 ? timeOffset : 0;
      const comparisonMatchTime = timeOffset < 0 ? -timeOffset : 0;
      
      // Calculate "earliness score" - lower is better (earlier)
      // Average of normalized positions (0 = start, 1 = end)
      const referenceDuration = channel1Data.length / sampleRate;
      const comparisonDuration = channel2Data.length / sampleRate;
      const referenceEarliness = referenceMatchTime / referenceDuration;
      const comparisonEarliness = comparisonMatchTime / comparisonDuration;
      const earlinessScore = (referenceEarliness + comparisonEarliness) / 2;

      console.log('[WaveformSync] Full-file match found', {
        timeOffset: timeOffset.toFixed(3),
        correlation: correlation.toFixed(4),
        referenceMatchTime: referenceMatchTime.toFixed(3),
        comparisonMatchTime: comparisonMatchTime.toFixed(3),
        earlinessScore: earlinessScore.toFixed(3),
      });

      // Search for matches in early portions (first 50% or 30 seconds, whichever is shorter)
      const maxEarlySearchDuration = 30; // seconds
      const earlySearchRatio = 0.5; // 50% of file
      
      const referenceEarlyDuration = Math.min(
        referenceDuration * earlySearchRatio,
        maxEarlySearchDuration
      );
      const comparisonEarlyDuration = Math.min(
        comparisonDuration * earlySearchRatio,
        maxEarlySearchDuration
      );
      
      const referenceEarlySamples = Math.floor(referenceEarlyDuration * sampleRate);
      const comparisonEarlySamples = Math.floor(comparisonEarlyDuration * sampleRate);

      // Only search early portions if the current match is not already early
      if (earlinessScore > 0.3) {
        try {
          // Strategy: Search early portion of BASE file against full COMPARISON file
          // This finds where the base's beginning (e.g., verse) matches in the comparison
          // This is more likely to find the earliest matching section
          const channel1Early = channel1Data.slice(0, referenceEarlySamples);

          const baseEarly = {
            channelData: [channel1Early],
            samplesDecoded: channel1Early.length,
          };

          const comparisonFull = {
            channelData: [channel2Data],
            samplesDecoded: channel2Data.length,
          };

          const { sampleOffset: earlySampleOffset, correlation: earlyCorrelation } = 
            await synAudio.syncWorkerConcurrent(baseEarly, comparisonFull, 4);

          const earlyTimeOffset = earlySampleOffset / sampleRate;
          
          // Calculate earliness for early match
          // We searched baseEarly (base's beginning) against comparisonFull
          // Positive offset means base's beginning (time 0) matches comparison at offset
          // So match is at 0 in base, at offset in comparison
          const earlyReferenceMatchTime = 0; // Always at start of base (reference)
          const earlyComparisonMatchTime = earlyTimeOffset > 0 ? earlyTimeOffset : 0;
          const earlyReferenceEarliness = 0; // Always at start
          const earlyComparisonEarliness = earlyComparisonMatchTime / comparisonDuration;
          const earlyEarlinessScore = (earlyReferenceEarliness + earlyComparisonEarliness) / 2;

          console.log('[WaveformSync] Early-portion match found (early vs full)', {
            timeOffset: earlyTimeOffset.toFixed(3),
            correlation: earlyCorrelation.toFixed(4),
            referenceMatchTime: earlyReferenceMatchTime.toFixed(3),
            comparisonMatchTime: earlyComparisonMatchTime.toFixed(3),
            earlinessScore: earlyEarlinessScore.toFixed(3),
          });

          // Prefer early match if:
          // 1. Its correlation is within threshold of best match (lowered threshold)
          // 2. It's significantly earlier
          const correlationRatio = earlyCorrelation / correlation;
          const earlinessImprovement = earlinessScore - earlyEarlinessScore;

          if (
            correlationRatio >= earlyMatchThreshold &&
            earlyEarlinessScore < earlinessScore &&
            earlinessImprovement > 0.05 // At least 5% improvement in earliness
          ) {
            console.log('[WaveformSync] Preferring early match', {
              correlationRatio: correlationRatio.toFixed(3),
              earlinessImprovement: earlinessImprovement.toFixed(3),
            });
            bestResult = {
              sampleOffset: earlySampleOffset,
              correlation: earlyCorrelation,
              timeOffset: earlyTimeOffset,
            };
          }
        } catch (earlyError) {
          console.warn('[WaveformSync] Early-portion search failed, using full-file result:', earlyError);
        }
      }
    }

    console.log('[WaveformSync] Synchronization complete', {
      sampleOffset: bestResult.sampleOffset,
      correlation: bestResult.correlation.toFixed(4),
      timeOffset: bestResult.timeOffset.toFixed(3),
      sampleRate,
    });

    return bestResult;
  } catch (error) {
    console.error('[WaveformSync] Synchronization failed:', error);
    return null;
  }
}

/**
 * Synchronizes multiple waveforms relative to a reference waveform
 * @param wavesurfers Map of player numbers to WaveSurfer instances
 * @param referencePlayer Player number to use as reference
 * @returns Map of player numbers to sync offsets (in seconds)
 */
export async function synchronizeAllWaveforms(
  wavesurfers: Map<number, WaveSurfer>,
  referencePlayer: number
): Promise<Map<number, number>> {
  const referenceWaveSurfer = wavesurfers.get(referencePlayer);
  if (!referenceWaveSurfer) {
    console.warn(`[WaveformSync] Reference player ${referencePlayer} not found`);
    return new Map();
  }

  const syncOffsets = new Map<number, number>();
  syncOffsets.set(referencePlayer, 0); // Reference has no offset

  // Synchronize all other players relative to the reference
  for (const [playerNumber, wavesurfer] of wavesurfers.entries()) {
    if (playerNumber === referencePlayer) {
      continue; // Skip reference player
    }

    console.log(`[WaveformSync] Synchronizing player ${playerNumber} to reference ${referencePlayer}`);
    
    // Try both directions and pick the best correlation
    // Enable early match preference to sync at earliest matching point
    // Lower threshold to 0.4 to catch early matches even if correlation is lower
    const result1 = await synchronizeWaveforms(referenceWaveSurfer, wavesurfer, {
      preferEarlyMatch: true,
      earlyMatchThreshold: 0.4, // Lower threshold to prefer early matches even with lower correlation
    });
    const result2 = await synchronizeWaveforms(wavesurfer, referenceWaveSurfer, {
      preferEarlyMatch: true,
      earlyMatchThreshold: 0.4,
    });
    
    let bestResult: SyncResult | null = null;
    let useReversed = false;
    
    if (result1 && result2) {
      // Calculate earliness scores for both results
      const referenceDuration = referenceWaveSurfer.getDuration();
      const comparisonDuration = wavesurfer.getDuration();
      
      // For result1: reference is base, comparison is compared
      // Positive offset means comparison starts later, so match is at offset in reference, 0 in comparison
      const result1RefTime = result1.timeOffset > 0 ? result1.timeOffset : 0;
      const result1CompTime = result1.timeOffset < 0 ? -result1.timeOffset : 0;
      const result1RefEarliness = referenceDuration > 0 ? result1RefTime / referenceDuration : 0;
      const result1CompEarliness = comparisonDuration > 0 ? result1CompTime / comparisonDuration : 0;
      const result1Earliness = (result1RefEarliness + result1CompEarliness) / 2;
      
      // For result2 (reversed): comparison is base, reference is compared
      // Positive offset means reference starts later, so match is at offset in comparison, 0 in reference
      const result2CompTime = result2.timeOffset > 0 ? result2.timeOffset : 0;
      const result2RefTime = result2.timeOffset < 0 ? -result2.timeOffset : 0;
      const result2RefEarliness = referenceDuration > 0 ? result2RefTime / referenceDuration : 0;
      const result2CompEarliness = comparisonDuration > 0 ? result2CompTime / comparisonDuration : 0;
      const result2Earliness = (result2RefEarliness + result2CompEarliness) / 2;
      
      const minCorrelationThreshold = 0.25; // Minimum correlation to consider a match valid
      const earlinessWeight = 0.5; // Weight for earliness vs correlation (0-1, higher = more weight on earliness)
      // Increased to 0.5 to strongly prefer earlier matches
      
      // Score both results considering both correlation and earliness
      // Lower earliness score = earlier = better
      const result1Score = result1.correlation * (1 - earlinessWeight) + (1 - result1Earliness) * earlinessWeight;
      const result2Score = result2.correlation * (1 - earlinessWeight) + (1 - result2Earliness) * earlinessWeight;
      
      console.log(`[WaveformSync] Comparing results for player ${playerNumber}`, {
        result1: {
          correlation: result1.correlation.toFixed(4),
          timeOffset: result1.timeOffset.toFixed(3),
          earliness: result1Earliness.toFixed(3),
          score: result1Score.toFixed(4),
        },
        result2: {
          correlation: result2.correlation.toFixed(4),
          timeOffset: result2.timeOffset.toFixed(3),
          earliness: result2Earliness.toFixed(3),
          score: result2Score.toFixed(4),
        },
      });
      
      // Prefer result with better score, but ensure minimum correlation threshold
      if (result1.correlation >= minCorrelationThreshold && result2.correlation >= minCorrelationThreshold) {
        // Both meet threshold, use combined score
        if (result1Score >= result2Score) {
          bestResult = result1;
        } else {
          bestResult = result2;
          useReversed = true;
        }
      } else if (result1.correlation >= minCorrelationThreshold) {
        // Only result1 meets threshold
        bestResult = result1;
      } else if (result2.correlation >= minCorrelationThreshold) {
        // Only result2 meets threshold
        bestResult = result2;
        useReversed = true;
      } else {
        // Neither meets threshold, use higher correlation anyway
        if (result1.correlation >= result2.correlation) {
          bestResult = result1;
        } else {
          bestResult = result2;
          useReversed = true;
        }
      }
    } else if (result1) {
      bestResult = result1;
    } else if (result2) {
      bestResult = result2;
      useReversed = true;
    }

    if (bestResult) {
      // If we used reversed comparison, negate the offset
      // Store the time offset (positive means comparison track content appears later in reference timeline)
      const offset = useReversed ? -bestResult.timeOffset : bestResult.timeOffset;
      syncOffsets.set(playerNumber, offset);
      console.log(`[WaveformSync] Player ${playerNumber} offset: ${offset.toFixed(3)}s (correlation: ${bestResult.correlation.toFixed(4)}, ${useReversed ? 'reversed' : 'normal'})`);
    } else {
      console.warn(`[WaveformSync] Failed to synchronize player ${playerNumber}`);
    }
  }

  return syncOffsets;
}

